import React, { useEffect, useRef, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { LandingPageContext } from "../../contexts/LandingPageContext";
import { useNavigate, useLocation } from "react-router-dom";
import "./NavMenu.css";

const NavMenu = () => {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const { scrollTo } = useContext(LandingPageContext);
    const navigate = useNavigate();
    const location = useLocation();
    const topLine = useRef();
    const middleLine = useRef();
    const bottomLine = useRef();
    const navPanel = useRef();

    useEffect(() => {
        gsap.set(navPanel.current, {
            scale: 0, autoAlpha: 0, transformOrigin: "bottom center",
            borderTopLeftRadius: 50, borderTopRightRadius: 50,
            width: "0%", left: "50%", xPercent: -50, padding: "40px 20px",
        });
    }, []);

    useEffect(() => {
        if (menuOpen) {
            gsap.to(topLine.current, { rotate: 45, y: 8, duration: 0.5, ease: "power3.out", backgroundColor: "#fff" });
            gsap.to(middleLine.current, { opacity: 0, duration: 0.2 });
            gsap.to(bottomLine.current, { rotate: -45, y: -8, duration: 0.5, ease: "power3.out", backgroundColor: "#fff" });
            gsap.to(navPanel.current, {
                scale: 1, autoAlpha: 1, width: "100%", left: "0%", xPercent: 0,
                borderTopLeftRadius: 0, borderTopRightRadius: 0, padding: 0,
                duration: 0.8, ease: "power3.out",
            });
        } else {
            gsap.to(topLine.current, { rotate: 0, y: 0, duration: 0.5, ease: "power3.in", backgroundColor: "#222" });
            gsap.to(middleLine.current, { opacity: 1, duration: 0.2 });
            gsap.to(bottomLine.current, { rotate: 0, y: 0, duration: 0.5, ease: "power3.in", backgroundColor: "#222" });
            gsap.to(navPanel.current, {
                scale: 0, autoAlpha: 0, width: "0%", left: "50%", xPercent: -50,
                borderTopLeftRadius: 50, borderTopRightRadius: 50, padding: "40px 20px",
                duration: 0.7, ease: "power3.in",
            });
        }
    }, [menuOpen]);

    useEffect(() => {
        document.body.classList.toggle("no-scroll", menuOpen);
        return () => document.body.classList.remove("no-scroll");
    }, [menuOpen]);

    const handleNavClick = (href) => {
        const isAnchor = href.startsWith("#");

        const computeOffset = () => {
            try {
                const headerEl = document.querySelector('.header');
                if (!headerEl) return 28;
                const rect = headerEl.getBoundingClientRect();
                return Math.max(0, Math.round(rect.top)) + Math.round(rect.height || 0) + 8;
            } catch { return 28; }
        };

        if (isAnchor) {
            if (location.pathname !== '/') {
                try { sessionStorage.setItem('skipSplash', '1'); } catch (e) {}
                try {
                    navigate(`/${href}`, { state: { targetAnchor: href } });
                } catch (e) {
                    navigate('/', { state: { targetAnchor: href } });
                }
            } else {
                const offset = computeOffset();
                if (typeof scrollTo === 'function') {
                    try { scrollTo(href, { offset }); } catch (e) { /* ignore */ }
                } else {
                    try {
                        const el = document.querySelector(href);
                        if (el) {
                            const targetY = el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - offset;
                            window.scrollTo({ top: targetY, behavior: 'smooth' });
                        }
                    } catch (e) { /* ignore */ }
                }
            }
        } else {
            navigate(href);
        }
        setMenuOpen(false);
    };

    const Logo = () => (
        <svg viewBox="0 0 262.7 262.7" preserveAspectRatio="xMidYMid meet" className="menu-logo">
            <g>
                <path stroke="#fff" strokeWidth="2" strokeMiterlimit="10" fill="#fff"
                    d="m77.9 50.9-57.3 57.3 5 4.9 52.3-52.3 50.9 51v24.6H81.2v7.1h47.6v74.1L79.3 168H65.7v7h10.7l59.4 59.5v-91h38.1l-17.8 17.8-5 4.9 23.4 23.4 4.9-5-18.4-18.4 29.8-29.8h-55V112L187 60.9l52.2 52.2 4.9-4.9L187 51l-51.2 51.1V27.6h-7v74.2L77.9 50.9z"
                />
            </g>
        </svg>
    );

    return (
        <div className="landing-page-burger">
            <button className={`menu-toggle ${menuOpen ? "open" : ""}`} onClick={() => setMenuOpen(prev => !prev)}>
                <span className="inner">
                    <span ref={topLine} className="line top"></span>
                    <span ref={middleLine} className="line middle"></span>
                    <span ref={bottomLine} className="line bottom"></span>
                </span>
            </button>

            <div ref={navPanel} id="navigation-slide">
                <h1 className="title">T A F E L B E R G</h1>
                <nav>
                    <ul>
                        <li>
                            <a href="#firstSection" onClick={(e) => { e.preventDefault(); handleNavClick("#firstSection"); }}>
                                {t('nav.idee')} <Logo />
                            </a>
                        </li>
                        <li>
                            <a href="#artSection" onClick={(e) => { e.preventDefault(); handleNavClick("#artSection"); }}>
                                {t('nav.einzigartig')} <Logo />
                            </a>
                        </li>
                        <li>
                            <a href="#nowSection" onClick={(e) => { e.preventDefault(); handleNavClick("#nowSection"); }}>
                                {t('nav.imJetzt')} <Logo />
                            </a>
                        </li>
                        <li>
                            <a href="/gallery" onClick={(e) => { e.preventDefault(); handleNavClick("/gallery"); }}>
                                {t('sections.gallery')} <Logo />
                            </a>
                        </li>
                        <li>
                            <a href="#contact" onClick={(e) => { e.preventDefault(); handleNavClick("#contact"); }}>
                                {t('nav.kontakt') || 'Contact'} <Logo />
                            </a>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    );
};

export default NavMenu;
